import { createAction, createReducer, on, props } from '@ngrx/store';
import { IAvailableJob, IJobSummaryDto, ISchedule } from 'src/app/core/dto';

/**
 * Provisional schedule moves.
 *
 * A schedule change goes UI -> Server -> Agent -> SCHEDULE.DBF -> sync -> estimate -> SignalR, and
 * with thousands of jobs that takes minutes. So when the user moves jobs between the Available and
 * Scheduled lists, the move is shown at once as a pending op layered over the server's collections,
 * and each job's provisional row drops away by itself once the server's lists agree.
 *
 * Rules (see the scheduler-large-moves plan):
 * - Pending ops live here, never in `collections.Schedule` / `AvailableJob`. The next server push of
 *   a Schedule doc would overwrite them there, and the jobs would jump back.
 * - Each list confirms on its own: the Schedule doc and the AvailableJob deletes arrive on separate
 *   streams. Available hides a job while its op is pending; Scheduled shows a provisional row only
 *   until `Schedule.sequence` has that job. An op forgets a job once both lists agree.
 * - The server always wins. Errors only decide what shows meanwhile: a definite failure removes the
 *   op, an unknown outcome (timeout) keeps it as `unconfirmed`, and every op expires when nothing
 *   lands for a while.
 * - Only real moves are shown: jobs already on the machine's schedule (re-sequencing), and jobs on
 *   the machine (a recall, which stays on the schedule) are left to the server.
 */

export type ScheduleOpKind = 'schedule' | 'unschedule';

/**
 * - `sending`: the request is still out.
 * - `accepted`: the server applied it; waiting for the sync to bring the lists into line.
 * - `unconfirmed`: we stopped waiting (timeout) but the Agent may still be applying it.
 */
export type ScheduleOpState = 'sending' | 'accepted' | 'unconfirmed';

export type ScheduleSequenceEntry = ISchedule['sequence'][number];

export interface PendingScheduleOp {
  opId: string;
  kind: ScheduleOpKind;
  machineNumber: number;
  /** Jobs whose move the server's lists don't show yet. Shrinks as the sync lands. */
  ordIds: number[];
  requestedSequenceNumber?: number;
  isOnMachine: boolean;
  /** Schedule ops: the provisional Scheduled rows, by ordId. */
  scheduleRows: Record<number, ScheduleSequenceEntry>;
  /** Unschedule ops: the provisional Available rows, by ordId. */
  availableRows: Record<number, IAvailableJob>;
  state: ScheduleOpState;
  createdAt: number;
  /** When the request came back or a job last landed. Expiry counts from here. */
  lastProgressAt: number;
}

export interface ScheduleOverlayState {
  ops: PendingScheduleOp[];
}

export const initialScheduleOverlayState: ScheduleOverlayState = { ops: [] };

/**
 * How long an op may go without progress before it is dropped and the server's lists show as they
 * are. Counted from `lastProgressAt`, so a big batch whose jobs keep landing never expires.
 */
export const SCHEDULE_OP_IDLE_LIMIT_MS: Record<ScheduleOpState, number> = {
  // The request itself is bounded by the server's Agent deadline (at most 10 minutes).
  sending: 15 * 60_000,
  accepted: 2 * 60_000,
  unconfirmed: 5 * 60_000,
};

// ---------------------------------------------------------------------------------------------
// Actions

export const scheduleOpStarted = createAction(
  '[ScheduleOverlay] op started',
  props<{ op: PendingScheduleOp }>()
);
export const scheduleOpAccepted = createAction(
  '[ScheduleOverlay] op accepted',
  props<{ opId: string; at: number }>()
);
export const scheduleOpUnconfirmed = createAction(
  '[ScheduleOverlay] op unconfirmed',
  props<{ opId: string; at: number }>()
);
export const scheduleOpFailed = createAction(
  '[ScheduleOverlay] op failed',
  props<{ opId: string }>()
);
export const scheduleOverlayPruned = createAction(
  '[ScheduleOverlay] pruned',
  props<{ landed: Record<string, number[]>; expired: string[]; at: number }>()
);

// ---------------------------------------------------------------------------------------------
// Reducer

const updateOp = (
  state: ScheduleOverlayState,
  opId: string,
  patch: Partial<PendingScheduleOp>
): ScheduleOverlayState =>
  state.ops.some((op) => op.opId === opId)
    ? { ops: state.ops.map((op) => (op.opId === opId ? { ...op, ...patch } : op)) }
    : state;

export const scheduleOverlayReducer = createReducer(
  initialScheduleOverlayState,
  on(scheduleOpStarted, (state, { op }) => ({ ops: [...state.ops, op] })),
  on(scheduleOpAccepted, (state, { opId, at }) =>
    updateOp(state, opId, { state: 'accepted', lastProgressAt: at })
  ),
  on(scheduleOpUnconfirmed, (state, { opId, at }) =>
    updateOp(state, opId, { state: 'unconfirmed', lastProgressAt: at })
  ),
  on(scheduleOpFailed, (state, { opId }) =>
    state.ops.some((op) => op.opId === opId)
      ? { ops: state.ops.filter((op) => op.opId !== opId) }
      : state
  ),
  on(scheduleOverlayPruned, (state, { landed, expired, at }) => {
    const expiredIds = new Set(expired);
    const ops = state.ops
      .filter((op) => !expiredIds.has(op.opId))
      .map((op) => {
        const done = landed[op.opId];
        if (!done?.length) {
          return op;
        }
        const doneIds = new Set(done);
        return { ...op, ordIds: op.ordIds.filter((id) => !doneIds.has(id)), lastProgressAt: at };
      })
      .filter((op) => op.ordIds.length > 0);
    return { ops };
  })
);

// ---------------------------------------------------------------------------------------------
// Building ops

let opCounter = 0;

/** Unique within this tab. (crypto.randomUUID needs a secure context, which plants often lack.) */
export const newScheduleOpId = () => `${Date.now().toString(36)}-${++opCounter}`;

const scheduleFor = (schedules: ISchedule[], machineNumber: number) =>
  schedules.find((s) => s.machineNumber === machineNumber);

/** A Scheduled row for a job that is still in the Available list. It has no estimate yet. */
export function toProvisionalScheduleEntry(
  available: IAvailableJob,
  machineNumber: number
): ScheduleSequenceEntry {
  // IAvailableJob shares most of its field names with IJobSummaryDto; fill in the rest.
  const job = {
    ...available,
    id: String(available.ordId),
    machineNumber,
    isOnMachine: false,
    quantity: available.totalPieces,
    quantityDone: available.totalPieces - available.remainingPieces,
    completeFt: available.totalFt - available.remainingFt,
    startDateTime: undefined,
    completionDate: undefined,
    sequence: undefined,
    scheduleState: { state: 'Scheduled', machineNumber, sequence: undefined, isOnMachine: false },
    provisional: true,
  } as unknown as IJobSummaryDto;

  return {
    // Same id the server gives the row (its ordId), so the row keeps its identity when it lands.
    id: String(available.ordId),
    ordId: available.ordId,
    machineNumber,
    isOnMachine: false,
    sequenceNum: 0, // placed by applyOverlayToSchedules
    warningDueDate: false,
    pastDueDate: false,
    warningMaterial: false,
    remainginRuntime: undefined,
    pastDueText: '',
    actionInProgress: false,
    warningText: '',
    provisional: true,
    job,
  };
}

/** An Available row for a job that is still on the schedule. */
export function toProvisionalAvailableJob(job: IJobSummaryDto, machineNumber: number): IAvailableJob {
  return {
    ...(job as unknown as IAvailableJob),
    id: `provisional/${machineNumber}/${job.ordId}`,
    machineNumber,
    ordId: job.ordId,
    expectedRuntime: 0,
    warningDueDate: false,
    pastDueDate: null,
    totalPieces: job.quantity,
    remainingPieces: job.quantity - job.quantityDone,
    provisional: true,
  };
}

/**
 * The pending op for a "schedule these jobs" request, or null when none of them is a move this
 * overlay shows (all already on the machine's schedule, or not in the Available list here).
 */
export function buildScheduleOp(
  opId: string,
  payload: {
    jobIds: number[];
    machineId: number;
    requestedSequenceNumber?: number;
    isOnMachine: boolean;
  },
  schedules: ISchedule[],
  availableJobs: IAvailableJob[],
  now: number
): PendingScheduleOp | null {
  const machineNumber = payload.machineId;
  const onSchedule = new Set(
    (scheduleFor(schedules, machineNumber)?.sequence ?? []).map((e) => e.ordId)
  );
  // A job available on several machines has a row per machine; prefer this machine's.
  const available = new Map<number, IAvailableJob>();
  for (const a of availableJobs) {
    if (!available.has(a.ordId) || a.machineNumber === machineNumber) {
      available.set(a.ordId, a);
    }
  }

  const ordIds: number[] = [];
  const scheduleRows: Record<number, ScheduleSequenceEntry> = {};
  for (const ordId of payload.jobIds ?? []) {
    // Already scheduled here: a re-sequence, not a move. The server's order is what shows.
    if (onSchedule.has(ordId) || scheduleRows[ordId]) {
      continue;
    }
    const row = available.get(ordId);
    if (!row || row.provisional) {
      continue;
    }
    scheduleRows[ordId] = toProvisionalScheduleEntry(row, machineNumber);
    ordIds.push(ordId);
  }

  if (ordIds.length === 0) {
    return null;
  }
  return {
    opId,
    kind: 'schedule',
    machineNumber,
    ordIds,
    requestedSequenceNumber: payload.requestedSequenceNumber,
    isOnMachine: !!payload.isOnMachine,
    scheduleRows,
    availableRows: {},
    state: 'sending',
    createdAt: now,
    lastProgressAt: now,
  };
}

/**
 * The pending op for an "unschedule these jobs" request, or null when none of them moves. Only
 * queued jobs move back to Available; a job on the machine is recalled and stays on the schedule
 * until the controller lets go of it, so the server's row (with its pending-action bar) shows that.
 */
export function buildUnscheduleOp(
  opId: string,
  payload: { scheduledJobIds: number[]; machineId: number },
  schedules: ISchedule[],
  availableJobs: IAvailableJob[],
  now: number
): PendingScheduleOp | null {
  const machineNumber = payload.machineId;
  const entries = new Map(
    (scheduleFor(schedules, machineNumber)?.sequence ?? []).map((e) => [e.ordId, e])
  );
  const availableHere = new Set(
    availableJobs.filter((a) => a.machineNumber === machineNumber).map((a) => a.ordId)
  );

  const ordIds: number[] = [];
  const availableRows: Record<number, IAvailableJob> = {};
  for (const ordId of payload.scheduledJobIds ?? []) {
    const entry = entries.get(ordId);
    if (!entry?.job || entry.isOnMachine || availableHere.has(ordId) || availableRows[ordId]) {
      continue;
    }
    availableRows[ordId] = toProvisionalAvailableJob(entry.job, machineNumber);
    ordIds.push(ordId);
  }

  if (ordIds.length === 0) {
    return null;
  }
  return {
    opId,
    kind: 'unschedule',
    machineNumber,
    ordIds,
    isOnMachine: false,
    scheduleRows: {},
    availableRows,
    state: 'sending',
    createdAt: now,
    lastProgressAt: now,
  };
}

// ---------------------------------------------------------------------------------------------
// Applying ops to the server's collections

/**
 * Where a schedule op's rows go in a machine's (sorted, server + earlier provisional) rows. Mirrors
 * the Agent: a requested sequence goes before the first queued job at or after it, but never ahead
 * of the jobs on the machine; a send goes right after the jobs on the machine; anything else goes
 * at the end.
 */
function insertionIndex(rows: ScheduleSequenceEntry[], op: PendingScheduleOp): number {
  let afterOnMachine = 0;
  rows.forEach((row, i) => {
    if (row.isOnMachine) {
      afterOnMachine = i + 1;
    }
  });
  if (op.isOnMachine) {
    return afterOnMachine;
  }
  if (op.requestedSequenceNumber != null) {
    const at = rows.findIndex(
      (row, i) =>
        i >= afterOnMachine &&
        !row.provisional &&
        row.job?.sequence != null &&
        row.job.sequence >= op.requestedSequenceNumber
    );
    if (at >= 0) {
      return at;
    }
  }
  return rows.length;
}

function applyToSchedule(schedule: ISchedule, ops: PendingScheduleOp[]): ISchedule {
  const onServer = new Set(schedule.sequence.map((e) => e.ordId));
  const leaving = new Set<number>();
  for (const op of ops) {
    if (op.kind === 'unschedule') {
      op.ordIds.forEach((id) => leaving.add(id));
    }
  }

  let changed = false;
  let rows = [...schedule.sequence]
    .filter((e) => {
      const keep = !leaving.has(e.ordId);
      changed = changed || !keep;
      return keep;
    })
    .sort((a, b) => a.sequenceNum - b.sequenceNum);

  for (const op of ops) {
    if (op.kind !== 'schedule') {
      continue;
    }
    const arriving = op.ordIds
      .filter((id) => !onServer.has(id))
      .map((id) => op.scheduleRows[id])
      .filter((row) => !!row);
    if (arriving.length === 0) {
      continue;
    }
    const at = insertionIndex(rows, op);
    // Fractional sequence numbers between the neighbours, so the server's rows keep theirs.
    const before = at > 0 ? rows[at - 1].sequenceNum : (rows[0]?.sequenceNum ?? 1) - 1;
    const after = at < rows.length ? rows[at].sequenceNum : before + arriving.length + 1;
    const step = (after - before) / (arriving.length + 1);
    const placed = arriving.map((row, i) => ({ ...row, sequenceNum: before + step * (i + 1) }));
    rows = [...rows.slice(0, at), ...placed, ...rows.slice(at)];
    changed = true;
  }

  return changed ? { ...schedule, sequence: rows } : schedule;
}

/**
 * The server's Schedule docs with pending ops applied: jobs being unscheduled are hidden, and jobs
 * being scheduled show as provisional rows until the server's doc has them. Returns `schedules`
 * itself when there is nothing to apply.
 */
export function applyOverlayToSchedules(
  schedules: ISchedule[],
  ops: PendingScheduleOp[]
): ISchedule[] {
  if (ops.length === 0) {
    return schedules;
  }
  const byMachine = new Map<number, PendingScheduleOp[]>();
  for (const op of [...ops].sort((a, b) => a.createdAt - b.createdAt)) {
    byMachine.set(op.machineNumber, [...(byMachine.get(op.machineNumber) ?? []), op]);
  }

  let changed = false;
  const result = schedules.map((schedule) => {
    const machineOps = byMachine.get(schedule.machineNumber);
    if (!machineOps) {
      return schedule;
    }
    byMachine.delete(schedule.machineNumber);
    const next = applyToSchedule(schedule, machineOps);
    changed = changed || next !== schedule;
    return next;
  });

  // A machine with nothing scheduled may have no doc yet.
  byMachine.forEach((machineOps, machineNumber) => {
    const next = applyToSchedule(
      { id: String(machineNumber), machineNumber, sequence: [] },
      machineOps
    );
    if (next.sequence.length > 0) {
      result.push(next);
      changed = true;
    }
  });

  return changed ? result : schedules;
}

/**
 * The server's AvailableJob rows with pending ops applied: jobs being scheduled are hidden (on every
 * machine, since scheduling removes all of a job's rows), and jobs being unscheduled show as
 * provisional rows until the server has a row for them. Returns `availableJobs` itself when there
 * is nothing to apply.
 */
export function applyOverlayToAvailable(
  availableJobs: IAvailableJob[],
  ops: PendingScheduleOp[]
): IAvailableJob[] {
  if (ops.length === 0) {
    return availableJobs;
  }
  const leaving = new Set<number>();
  const returning: PendingScheduleOp[] = [];
  for (const op of ops) {
    if (op.kind === 'schedule') {
      op.ordIds.forEach((id) => leaving.add(id));
    } else {
      returning.push(op);
    }
  }

  const kept = leaving.size
    ? availableJobs.filter((a) => !leaving.has(a.ordId))
    : availableJobs;

  const added: IAvailableJob[] = [];
  if (returning.length) {
    const onServer = new Set(availableJobs.map((a) => `${a.machineNumber}:${a.ordId}`));
    for (const op of returning) {
      for (const ordId of op.ordIds) {
        const row = op.availableRows[ordId];
        if (row && !onServer.has(`${op.machineNumber}:${ordId}`)) {
          added.push(row);
        }
      }
    }
  }

  if (kept === availableJobs && added.length === 0) {
    return availableJobs;
  }
  return added.length ? [...kept, ...added] : kept;
}

// ---------------------------------------------------------------------------------------------
// Confirming and expiring ops

/**
 * Per op, the jobs whose move both server lists now show. A scheduled job has landed when it is in
 * the machine's Schedule doc and no AvailableJob row is left for it; an unscheduled job when it is
 * out of the Schedule doc and has an AvailableJob row for the machine.
 */
export function findLandedJobs(
  ops: PendingScheduleOp[],
  schedules: ISchedule[],
  availableJobs: IAvailableJob[]
): Record<string, number[]> {
  const landed: Record<string, number[]> = {};
  if (ops.length === 0) {
    return landed;
  }
  const scheduledByMachine = new Map<number, Set<number>>();
  for (const s of schedules) {
    scheduledByMachine.set(s.machineNumber, new Set(s.sequence.map((e) => e.ordId)));
  }
  const availableAnywhere = new Set<number>();
  const availableOn = new Set<string>();
  for (const a of availableJobs) {
    if (a.provisional) {
      continue;
    }
    availableAnywhere.add(a.ordId);
    availableOn.add(`${a.machineNumber}:${a.ordId}`);
  }

  for (const op of ops) {
    const scheduled = scheduledByMachine.get(op.machineNumber) ?? new Set<number>();
    const done = op.ordIds.filter((ordId) =>
      op.kind === 'schedule'
        ? scheduled.has(ordId) && !availableAnywhere.has(ordId)
        : !scheduled.has(ordId) && availableOn.has(`${op.machineNumber}:${ordId}`)
    );
    if (done.length) {
      landed[op.opId] = done;
    }
  }
  return landed;
}

/** Ops that have gone too long without progress for their state. */
export function findExpiredOps(ops: PendingScheduleOp[], now: number): PendingScheduleOp[] {
  return ops.filter((op) => now - op.lastProgressAt > SCHEDULE_OP_IDLE_LIMIT_MS[op.state]);
}

/** Every job with a pending move, for the scheduler's "still saving" guard. */
export function pendingOrdIds(ops: PendingScheduleOp[]): Set<number> {
  const ids = new Set<number>();
  for (const op of ops) {
    op.ordIds.forEach((id) => ids.add(id));
  }
  return ids;
}
